import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { InstallPrompt } from "./components/InstallPrompt.js";
import { NamePrompt } from "./components/NamePrompt.js";
import { PasswordGate } from "./components/PasswordGate.js";
import { useSyncAccount } from "./hooks/useSyncAccount.js";
import * as storage from "./lib/storage.js";
import { JoinPage } from "./pages/JoinPage.js";
import { ListPage } from "./pages/ListPage.js";
import { ListsPage } from "./pages/ListsPage.js";
import { NotFoundPage } from "./pages/NotFoundPage.js";
import type { StoredList } from "./types/index.js";
import "./App.css";

export function App() {
  const [displayName, setDisplayName] = useState(storage.getDisplayName());
  const [lists, setLists] = useState<StoredList[]>(storage.getLists());
  const [restoring, setRestoring] = useState(false);
  const [syncAccountId, setSyncAccountId] = useState<string | null>(storage.getSyncAccountId());
  const [gateState, setGateState] = useState<"checking" | "required" | "passed">(
    import.meta.env.VITE_DEV_GATE === "true" ? "checking" : "passed",
  );

  useEffect(() => {
    if (gateState !== "checking") return;
    fetch("/api/dev/status")
      .then((r) => r.json())
      .then((data) => {
        if (!data.data?.required || data.data?.authenticated) {
          setGateState("passed");
        } else {
          setGateState("required");
        }
      })
      .catch(() => setGateState("passed"));
  }, [gateState]);

  // Attempt restore from server cookie when localStorage is empty
  // biome-ignore lint/correctness/useExhaustiveDependencies: run-once on mount
  useEffect(() => {
    if (displayName || lists.length > 0) return;

    let cancelled = false;
    setRestoring(true);

    storage.restoreFromServer().then((data) => {
      if (cancelled) return;
      if (data) {
        setDisplayName(data.displayName);
        setLists(data.lists);
      }
      setRestoring(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const handleNameSubmit = useCallback((name: string) => {
    storage.setDisplayName(name);
    setDisplayName(name);
  }, []);

  const refreshLists = useCallback(() => {
    setLists(storage.getLists());
  }, []);

  const handleSyncChange = useCallback((id: string | null) => {
    setSyncAccountId(id);
    // Refresh lists after sync change (mergedData may have updated localStorage)
    setLists(storage.getLists());
    setDisplayName(storage.getDisplayName());
  }, []);

  useSyncAccount({
    syncAccountId,
    onRemoteChange: useCallback((data) => {
      setDisplayName(data.displayName);
      setLists(data.lists);
    }, []),
  });

  const primaryList = lists.find((l) => l.isPrimary) ?? lists[0];

  if (gateState === "checking" || restoring) {
    return (
      <main className="restoring">
        <output>読み込み中…</output>
      </main>
    );
  }

  if (gateState === "required") {
    return <PasswordGate onVerified={() => setGateState("passed")} />;
  }

  // Show name prompt if no display name is set
  if (!displayName) {
    return <NamePrompt onSubmit={handleNameSubmit} />;
  }

  return (
    <BrowserRouter>
      <Routes>
        <Route
          path="/"
          element={
            primaryList ? <ListPage currentList={primaryList} /> : <Navigate to="/lists" replace />
          }
        />
        <Route path="/l/:shareToken" element={<JoinPage onListsChange={refreshLists} />} />
        <Route
          path="/lists"
          element={
            <ListsPage
              lists={lists}
              syncAccountId={syncAccountId}
              onListsChange={refreshLists}
              onSyncChange={handleSyncChange}
            />
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <InstallPrompt />
    </BrowserRouter>
  );
}
